import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Player, GameState } from '../types';
import { FaDoorOpen, FaDice } from 'react-icons/fa';
import { FaGripLines, FaCheck, FaRotateLeft } from 'react-icons/fa6';
import Cover from '../media/TripleFreezerCover2.jpg';

interface Props {
  players: Player[];
  gameState: GameState;
  onStartNextMatch: (newOrder: { id: string; name: string }[]) => void;
  onQuit?: () => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: calc(60px + env(safe-area-inset-top, 0px));
  padding-left: ${({ theme }) => theme.spacing(2)};
  padding-right: ${({ theme }) => theme.spacing(2)};
  padding-bottom: ${({ theme }) => theme.spacing(2)};
  min-height: 100vh;
  width: 100%;
  position: relative;
  background: url(${Cover}) no-repeat center center;
  background-size: cover;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: rgba(0, 13, 26, 1);
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0 20px;
  z-index: 10;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  text-transform: uppercase;
`;

const MatchHeading = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 1);
  font-family: 'Fjalla One', sans-serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 30px;
  text-align: center;
  text-transform: uppercase;
  text-shadow: 0 0 5px rgba(0, 0, 0, 1);
  background: radial-gradient(4050% 50% at 50% 50%, rgba(255, 255, 255, 0.08), rgba(0, 170, 255, 0) 100%);
  margin-bottom: 20px;
`;

const PlayerStatsCard = styled.div<{ isWinner: boolean; isLeader: boolean; isTied?: boolean }>`
  width: 320px;
  min-height: 180px;
  box-sizing: border-box;
  border: ${({ isWinner, isLeader }) => 
    isWinner ? '2px solid rgba(255, 213, 0, 1)' : 
    isLeader ? '2px solid rgba(0, 255, 85, 1)' : 
    '2px solid rgba(128, 128, 128, 1)'};
  border-radius: 10px;
  box-shadow: ${({ isWinner, isLeader }) => 
    isWinner ? '0px 0px 20px 0px rgba(255, 170, 0, 1)' : 
    isLeader ? '0px 0px 15px 0px rgba(0, 255, 85, 0.5)' :
    '0px 0px 5px 0px rgba(0, 0, 0, 0.5)'};
  background: rgba(0, 32, 64, 0.75);
  margin-bottom: 16px;
  padding: 10px;
  display: flex;
  flex-direction: column;
`;

const PlayerHeader = styled.div`
  width: 280px;
  height: 40px;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 23px;
  text-align: center;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
`;

const StatsContent = styled.div`
  width: 280px;
  min-height: 120px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  padding: 10px 0;
`;

const StatsRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const StatsColumn = styled.div`
  width: 140px;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  text-align: left;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-bottom: 20px;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  width: 140px;
  height: 40px;
  border-radius: 5px;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.5);
  background: ${({ $variant }) => 
    $variant === 'secondary' 
      ? 'linear-gradient(180deg, rgba(255, 64, 0, 1), rgba(128, 32, 0, 1) 100%)'
      : 'linear-gradient(180deg, rgba(0, 127, 255, 1), rgba(0, 32, 64, 1) 100%)'
  };
  color: rgba(255, 255, 255, 1);
  border: none;
  cursor: pointer;
  font-family: 'Fjalla One', sans-serif;
  font-size: 18px;
  font-weight: 400;
  text-align: center;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 127, 255, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ReorderModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  touch-action: none;
  overscroll-behavior: contain;
`;

const ModalContent = styled.div`
  background: rgba(0, 32, 64, 1);
  border-radius: 10px;
  width: 300px;
  height: 240px;
  box-shadow: 0px 5px 5px 0px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  width: 300px;
  height: 40px;
  background: rgba(0, 13, 26, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 1);
  font-family: 'Fjalla One', sans-serif;
  font-size: 16px;
  text-transform: uppercase;
`;

const ModalBody = styled.div`
  padding: 10px 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: none;
  overscroll-behavior: contain;
  overflow: hidden;
`;

const ModalDescription = styled.div`
  width: 260px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  text-align: center;
  margin: 0 auto 6px auto;
`;

const PlayerReorderItem = styled.div`
  width: 260px;
  height: 40px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  border-top: 1px solid rgba(128, 128, 128, 1);
  border-bottom: 1px solid rgba(128, 128, 128, 1);
  margin: 0 auto;
  cursor: grab;
  touch-action: none;
`;

const Grip = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 1);
`;

const PlayerName = styled.span`
  flex: 1;
  margin-left: 8px;
`;

// No arrow buttons; drag-and-drop only

const ModalFooter = styled.div`
  padding: 8px 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FooterButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  width: ${({ $variant }) => ($variant === 'secondary' ? '110px' : '130px')};
  border-radius: 5px;
  border: none;
  color: rgba(255, 255, 255, 1);
  font-family: 'Fjalla One', sans-serif;
  font-size: 24px;
  text-transform: uppercase;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.5);
  background: ${({ $variant }) =>
    $variant === 'secondary'
      ? 'linear-gradient(180deg, rgba(255, 64, 0, 1), rgba(64, 16, 0, 1) 100%)'
      : 'linear-gradient(180deg, rgba(0, 127, 255, 1), rgba(0, 32, 64, 1) 100%)'};
  
  svg {
    width: 24px;
    height: 24px;
    flex: 0 0 auto;
  }
`;

// Reuse history styling from WinnerScreen
const HistoryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`;

const HistoryLabel = styled.span`
  width: 46px;
  height: 19px;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  text-align: left;
`;

const HistoryToggle = styled.div<{ $on: boolean }>`
  width: 40px;
  height: 20px;
  border-radius: 10px;
  background: rgba(128, 128, 128, 1);
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const HistoryKnob = styled.div<{ $on: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 1);
  position: absolute;
  left: ${({ $on }) => ($on ? '20px' : '0px')};
  transition: left 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const HistoryContainer = styled.div`
  margin-top: 10px;
  width: 260px;
  height: 80px;
  border-radius: 10px;
  background: rgba(0, 13, 26, 1);
  padding: 10px;
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
`;

const HistoryScroll = styled.div`
  width: 240px;
  height: 60px;
  max-height: 60px;
  overflow-y: auto;
  color: rgba(255, 255, 255, 1);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  text-align: left;

  /* Custom scrollbar */
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: rgba(255, 255, 255, 1) transparent; /* Firefox */

  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 1);
    border-radius: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const HistoryItem = styled.div`
  margin-bottom: 4px;
`;

  const DieValue = styled.span<{ $frozen?: boolean }>`
    color: ${({ $frozen }) => ($frozen ? 'rgba(0, 191, 230, 1)' : 'rgba(255, 255, 255, 1)')};
    font-weight: ${({ $frozen }) => ($frozen ? 700 : 400)};
  `;

const MatchStats: React.FC<Props> = ({ players, gameState, onStartNextMatch, onQuit }) => {
  console.log('Rendering MatchStats', { players, gameState });
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [playersToReorder, setPlayersToReorder] = useState<{ id: string; name: string }[]>(
    players.map(p => ({ id: p.id, name: p.name }))
  );
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchStartTime, setTouchStartTime] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // touchActiveIndex not needed for this modal; using simple drag gesture
  const reorderListRef = useRef<HTMLDivElement>(null);
  const DRAG_THRESHOLD = 12;
  const LONG_PRESS_DURATION = 120;
  const [playerHistoryStates, setPlayerHistoryStates] = useState<{ [key: string]: boolean }>({});

  // Prefer finalized snapshot from last match to avoid race conditions
  const lastMatch = gameState.matchHistory[gameState.matchHistory.length - 1];
  const playerStatsById: Record<string, {
    playerId: string;
    name: string;
    score: number;
    throws: number;
    accuracy: number;
    triples: number;
    tripleTriples: number;
    finalThrows: number;
    maxTurnScore: number;
    frozenDiceCount: number;
    history: any[];
  }> = (lastMatch?.playerStats || []).reduce((acc: any, ps: any) => {
    acc[ps.playerId] = ps;
    return acc;
  }, {});

  // Sort players by matchWins (series score) for display
  const sortedPlayers = [...players].sort((a, b) => (b.matchWins || 0) - (a.matchWins || 0));
  const currentMatchWinner = lastMatch ? lastMatch.winner : undefined;



  // Up/Down handlers removed; drag-and-drop only

  const handleConfirmReorder = () => {
    onStartNextMatch(playersToReorder);
    setShowReorderModal(false);
  };

  const handleTouchStart = (e: React.TouchEvent, index: number) => {
    const touch = e.touches[0];
    setTouchStartY(touch.clientY);
    setTouchStartX(touch.clientX);
    setTouchStartTime(Date.now());
    setDraggedIndex(index);
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setDraggedIndex(null);
    setTouchStartY(null);
    setTouchStartX(null);
    setTouchStartTime(null);
    setIsDragging(false);
  };

  const handleTouchMoveNative = useCallback((e: TouchEvent) => {
    if (draggedIndex === null || touchStartY === null || touchStartX === null || touchStartTime === null) return;
    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches[0];
    const currentY = touch.clientY;
    const currentX = touch.clientX;
    const diffY = Math.abs(currentY - touchStartY);
    const diffX = Math.abs(currentX - touchStartX);
    const timeDiff = Date.now() - touchStartTime;
    if (!isDragging) {
      const shouldStart = (diffY > DRAG_THRESHOLD && diffY > diffX) || (timeDiff > LONG_PRESS_DURATION && (diffY > 5 || diffX > 5));
      if (!shouldStart) return;
      setIsDragging(true);
      if (navigator.vibrate) navigator.vibrate(30);
      return;
    }
    const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!elementBelow) return;
    const item = elementBelow.closest('[data-reorder-index]') as HTMLElement | null;
    if (!item) return;
    const targetIndex = parseInt(item.getAttribute('data-reorder-index') || '0');
    if (targetIndex !== draggedIndex && targetIndex >= 0 && targetIndex < playersToReorder.length) {
      setPlayersToReorder(prev => {
        const list = [...prev];
        const dragged = list[draggedIndex];
        list.splice(draggedIndex, 1);
        list.splice(targetIndex, 0, dragged);
        return list;
      });
      setDraggedIndex(targetIndex);
      if (navigator.vibrate) navigator.vibrate(15);
    }
  }, [draggedIndex, touchStartY, touchStartX, touchStartTime, isDragging, playersToReorder.length]);

  useEffect(() => {
    if (!showReorderModal) return;
    const el = reorderListRef.current;
    if (!el) return;
    const preventDefault = (evt: TouchEvent) => {
      if (evt.cancelable) evt.preventDefault();
    };
    el.addEventListener('touchmove', handleTouchMoveNative, { passive: false });
    el.addEventListener('touchstart', preventDefault, { passive: false });
    el.addEventListener('touchend', preventDefault, { passive: false });
    return () => {
      el.removeEventListener('touchmove', handleTouchMoveNative as any);
      el.removeEventListener('touchstart', preventDefault as any);
      el.removeEventListener('touchend', preventDefault as any);
    };
  }, [showReorderModal, handleTouchMoveNative]);

  const togglePlayerHistory = (playerId: string) => {
    setPlayerHistoryStates(prev => ({
      ...prev,
      [playerId]: !prev[playerId]
    }));
  };

  return (
    <Container>
      <Header>TRIPLE FREEZER</Header>
      
      <MatchHeading>MATCH {gameState.currentMatch}/{gameState.totalMatches} RESULTS</MatchHeading>

      <ButtonContainer>
        <ActionButton onClick={() => setShowReorderModal(true)}>
          <FaDice size={18} />
          Continue
        </ActionButton>
        <ActionButton $variant="secondary" onClick={() => onQuit && onQuit()}>
          <FaDoorOpen size={18} />
          Quit
        </ActionButton>
      </ButtonContainer>

        {sortedPlayers.map((player, index) => {
        const playerStats = playerStatsById[player.id];
          const isWinner = player.name === currentMatchWinner;
          const isLeader = index === 0;
        
        // Calculate wins and losses
        const wins = player.matchWins || 0;
        const losses = gameState.currentMatch - wins;
        
        const showHistory = playerHistoryStates[player.id] || false;
          return (
            <PlayerStatsCard 
              key={player.id} 
              isWinner={isWinner}
              isLeader={isLeader}
              isTied={false}
            >
            <PlayerHeader>
              {isWinner ? '🏆 ' : ''}
              {player.name} {wins} - {losses}
            </PlayerHeader>
            
            <StatsContent>
              <StatsRow>
                <StatsColumn>
                  <div>Matches Won:</div>
                  <div>{wins}</div>
                  <div>Win Rate:</div>
                  <div>{gameState.currentMatch > 0 ? 
                    (((wins) / gameState.currentMatch) * 100).toFixed(1) : 0}%</div>
                  <div>This Match Score:</div>
                  <div>{playerStats ? playerStats.score : player.score}</div>
                  <div>This Match Accuracy:</div>
                  <div>{!playerStats ? (player.history.length === 0 ? 'No throws' : '—') : `${playerStats.accuracy.toFixed ? playerStats.accuracy.toFixed(2) : playerStats.accuracy}%`}</div>
                </StatsColumn>
                <StatsColumn>
                  <div>Triples:</div>
                  <div>{!playerStats ? (player.history.length === 0 ? 'No throws' : '—') : playerStats.triples}</div>
                  <div>Triple Triples:</div>
                  <div>{!playerStats ? (player.history.length === 0 ? 'No throws' : '—') : playerStats.tripleTriples}</div>
                  <div>Final Throws:</div>
                  <div>{!playerStats ? (player.history.length === 0 ? 'No throws' : '—') : playerStats.finalThrows}</div>
                  <div>Frozen Dice:</div>
                  <div>{!playerStats ? (player.history.length === 0 ? 'No throws' : '—') : playerStats.frozenDiceCount}</div>
                  <div>Max Turn Score:</div>
                  <div>{!playerStats ? (player.history.length === 0 ? 'No throws' : '—') : playerStats.maxTurnScore}</div>
                </StatsColumn>
              </StatsRow>

              <HistoryRow>
                <HistoryLabel>History</HistoryLabel>
                <HistoryToggle $on={showHistory} onClick={() => togglePlayerHistory(player.id)}>
                  <HistoryKnob $on={showHistory} />
                </HistoryToggle>
              </HistoryRow>

              {showHistory && (
                <HistoryContainer>
                  <HistoryScroll>
                    {(() => {
                      const history = playerStats?.history || player.history;
                      if (!history || history.length === 0) return <div>No history available.</div>;
                      return history.map((entry: any, index: number) => {
                        const isTriple = entry.diceValues.every((v: number) => v > 0);
                        const uniqueVals: number[] = Array.from(new Set<number>(entry.diceValues as number[]));
                        const isTripleTriple = uniqueVals.length === 1 && uniqueVals[0] >= 2 && uniqueVals[0] <= 6;
                        const isAllSix = isTripleTriple && uniqueVals[0] === 6;
                        const isMiss = entry.diceValues.every((v: number) => v === 0);
                        const noFrozen = Array.isArray(entry.isFrozen) ? entry.isFrozen.every((f: boolean) => !f) : true;
                        const frozenSix = entry.isFrozen?.some((f: boolean, i: number) => f && entry.diceValues[i] === 6);
                        const nonFrozenPositive = entry.diceValues.filter((v: number, i: number) => !entry.isFrozen?.[i] && v > 0).length;
                        const tags: string[] = [];
                        if (!isAllSix && entry.isFinalThrowAttempt) tags.push('Final');
                        if (isAllSix) {
                          tags.push('Insta Win');
                        } else if (isTripleTriple) {
                          tags.push('Triple Triple');
                        } else {
                          if (entry.hasWonAttempt) tags.push('Win');
                          if (noFrozen && isTriple) tags.push('Triple');
                        }
                        if (isMiss) tags.push('Miss');
                        if (frozenSix && nonFrozenPositive === 2) {
                          tags.push('Frozen x2');
                        }
                        return (
                        <HistoryItem key={index}>
                            Turn {index + 1}: [
                            {entry.diceValues.map((v: number, i: number) => (
                              <>
                                <DieValue key={i} $frozen={!!entry.isFrozen?.[i]}>{v}</DieValue>
                                {i < entry.diceValues.length - 1 ? ', ' : ''}
                              </>
                            ))}
                            ] = {entry.turnScore} pts{tags.length ? ` (${tags.join(', ')})` : ''}
                        </HistoryItem>
                        );
                      });
                    })()}
                  </HistoryScroll>
                </HistoryContainer>
              )}
            </StatsContent>
            </PlayerStatsCard>
          );
        })}



      {showReorderModal && (
        <ReorderModal>
          <ModalContent>
            <ModalHeader>REORDER PLAYERS</ModalHeader>
            <ModalBody ref={reorderListRef}>
              <ModalDescription>Set player order for the next match:</ModalDescription>
              {playersToReorder.map((player, index) => (
                <PlayerReorderItem
                  key={player.id}
                  data-reorder-index={index}
                  draggable
                  onDragStart={() => setDraggedIndex(index)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (draggedIndex !== null && draggedIndex !== index) {
                      const list = [...playersToReorder];
                      const item = list[draggedIndex];
                      list.splice(draggedIndex, 1);
                      list.splice(index, 0, item);
                      setPlayersToReorder(list);
                      setDraggedIndex(index);
                    }
                  }}
                  onDragEnd={() => setDraggedIndex(null)}
                  onTouchStart={(e) => handleTouchStart(e, index)}
                  onTouchEnd={handleTouchEnd}
                >
                  <Grip>
                    <FaGripLines size={32} />
                  </Grip>
                  <PlayerName>{player.name}</PlayerName>
                </PlayerReorderItem>
              ))}
            </ModalBody>
            <ModalFooter>
              <FooterButton $variant="secondary" onClick={() => setShowReorderModal(false)}>
                <FaRotateLeft />
                Cancel
              </FooterButton>
              <FooterButton onClick={() => { handleConfirmReorder(); }}>
                <FaCheck />
                Confirm
              </FooterButton>
            </ModalFooter>
          </ModalContent>
        </ReorderModal>
      )}
    </Container>
  );
};

export default MatchStats;